import { C, hexToRgba, prefersReducedMotion } from '../../theme/tokens';

/**
 * The vault's own motion vocabulary. Three moments, no more — DESIGN.md's
 * budget is "one authored entrance per surface, then stillness", and a page
 * that animates on every state change reads as unstable rather than alive.
 *
 * Every helper returns `animation: 'none'` under prefers-reduced-motion but
 * keeps the colour and opacity transitions that carry meaning, which is what
 * "reduce motion" asks for rather than "remove all feedback".
 */

/** Total stagger ceiling. Past this the page feels like it is loading rather
 * than opening, however many compartments there are. */
const MAX_STAGGER_MS = 240;

export const stagger = (index: number, step = 60): number => Math.min(index * step, MAX_STAGGER_MS);

/**
 * The unseal: a compartment wipes open from its top edge rather than sliding
 * in from nowhere. clip-path is the one new material this page adds — it
 * reads as a drawer being drawn back, which a fade or a translate does not.
 */
export function unsealSx(index = 0) {
  if (prefersReducedMotion()) {
    return { animation: 'none' } as const;
  }
  return {
    animation: `vaultUnseal 0.5s cubic-bezier(0.16,1,0.3,1) ${stagger(index)}ms both`,
    '@keyframes vaultUnseal': {
      from: { clipPath: 'inset(0 0 100% 0)', opacity: 0 },
      to: { clipPath: 'inset(0 0 0 0)', opacity: 1 },
    },
  } as const;
}

/** Deposit confirm — one small settle, no bounce. A routine save should feel
 * certain, not celebrated. Carried over from the previous page unchanged
 * because it was already right. */
export const confirmChirpSx = {
  animation: prefersReducedMotion() ? 'none' : 'vaultChirp 0.35s cubic-bezier(0.16,1,0.3,1) both',
  '@keyframes vaultChirp': {
    from: { opacity: 0, transform: 'scale(0.85)' },
    to: { opacity: 1, transform: 'scale(1)' },
  },
} as const;

/**
 * A row the tracker added while you were watching. This is the one moment
 * that explains a change the user did not cause, so it gets a real signal:
 * a scanner-cyan sweep that fades to nothing and never repeats.
 *
 * Under reduced motion the ring is held as a static tint instead of pulsing —
 * the "this is new" information survives, the movement does not.
 */
export function trackedArrivalSx() {
  if (prefersReducedMotion()) {
    return { boxShadow: `0 0 0 1px ${hexToRgba(C.cyan, 0.45)}` } as const;
  }
  return {
    animation: 'vaultTrackedArrival 1.8s cubic-bezier(0.16,1,0.3,1) both',
    '@keyframes vaultTrackedArrival': {
      '0%': { boxShadow: `0 0 0 0 ${hexToRgba(C.cyan, 0.55)}`, borderColor: hexToRgba(C.cyan, 0.7) },
      '55%': { boxShadow: `0 0 18px 2px ${hexToRgba(C.cyan, 0.28)}` },
      '100%': { boxShadow: '0 0 0 0 rgba(0,0,0,0)' },
    },
  } as const;
}

/**
 * The vault's word for each share scope, in its own sealed-document voice.
 *
 * Defined once and imported by the rail, the summary and the reader, which
 * each carried a private copy — so adding a scope meant remembering three
 * files, and the compiler only caught it because `Record<ShareScope, ...>`
 * is exhaustive. Keep it that way rather than widening to a partial map.
 */
export const SEAL_LABEL: Record<import('../../types').ShareScope, string> = {
  private: 'SEALED',
  group: 'GROUP',
  corp: 'CORP',
  alliance: 'ALLIANCE',
  // Every reader is already past EVE SSO and the allowlist, so the widest
  // scope is "everyone in here", not "unsealed to the world".
  public: 'OPEN',
};
